import _ from 'lodash';

import { ID_DEFINITION, ID_GRAPH_AXIS } from '../../constants/idDefinition';
import { TABLE_DATA_SOURCE, UNITS_SIGN, CHART_TYPE, SIMULATION_TYPE } from '../../constants/definitionConstants';
import { ANALOG_BJT } from '../../constants/prtParametersNames';

export class analog_bjt {
	constructor(opn) {
		this.opn = opn;
		this.wpn = 'bcp53m';
		this.PACKAGE_IMAGE = require(`../../images/WDFNW3_CASE_515AA.png`);
		this.PIN_MAP = require(`../../images/${this.opn}_PM.svg`);
		this.SCHEMATIC_DC = require(`../../images/${this.opn}_SCH_DC.svg`);
		this.SCHEMATIC_TRAN = require(`../../images/${this.opn}_SCH_TRAN.svg`);
		this.modelLink = 'https://www.onsemi.com/download/models/lib/bcp53m_model.lib';
		this.netlistPathDC = `resources/${this.wpn}/${this.opn}/netlists/${this.opn}_DC.txt`;
		this.netlistPathTran = `resources/${this.wpn}/${this.opn}/netlists/${this.opn}_Trans.txt`;
		this.DC_CHART_COLUMN_MAPPING = {
			TEMP: 1,
			I_BASE: 2,
			I_COLLECTOR: 3,
			h_FE: 4,
			V_BASE: 5,
			V_COLLECTOR: 6
		};
		this.TRANSIENT_CHART_COLUMN_MAPPING = {
			TIME: 0,
			TEMP: 1,
			Vs_STEP: 2,
			I_BASE: 3,
			V_BASE: 4,
			V_EMITTER: 5,
			I_SUPPLY: 6,
			V_CE: 7,
			P_D: 8
		};
	}

	gainDefinition(customizations = {}) {
		const defaultDefinition = {
			/**
			 * Name of Page
			 */
			mainHeading: this.opn.toUpperCase(),
			wpn: this.wpn.toUpperCase(),

			/**
			 * is used to get description, table, primary and secondary links base parameter to pull data from WD+ database (value is OPN)
			 */
			defaultMappingName: this.opn.toUpperCase(),
			primaryLinks: [
				{
					/**
					 * used to identify element and map data from WD+
					 */
					id: ID_DEFINITION.PRODUCT_PAGE,
					description: 'Part Name',
					heading: this.wpn.toUpperCase()
				},
				{
					id: ID_DEFINITION.DATASHEET,
					description: 'Datasheet',
					heading: 'Open Datasheet'
				}
			],
			secondaryLinks: [
				{
					id: ID_DEFINITION.SIMILAR_PRODUCTS,
					heading: 'Find similar products'
				},
				{
					id: ID_DEFINITION.EVALUATION_BOARD,
					heading: 'Evaluation board'
				}
			],
			pinMap: {
				heading: 'Pin Map',
				imagePath: this.PIN_MAP
			},
			package: {
				heading: 'Package 3D',
				imagePath: this.PACKAGE_IMAGE,
				links: [
					{
						id: ID_DEFINITION.CASE_OUTLINE,
						heading: 'Case outline drawing'
					},
					{
						id: ID_DEFINITION.CASE_CAD,
						heading: 'CAD Model'
					}
				]
			},
			/**
			 * It is empty but it needs to be present, if not data wont load and description section will be empty.
			 */
			longDescription: {},
			table: {
				heading: 'Electrical Parameters',
				prtDataTaxonomy: ANALOG_BJT.TAXONOMY_ID,
				/**
				 * Format for table parameter data from PRT
				 * {
				 * 		dataSource: TABLE_DATA_SOURCE.PRT,
				 *		mappingName: POWER_SUPPLY.V_BR_DSS
				 * 	}
				 */
				data: [
					{
						dataSource: TABLE_DATA_SOURCE.PRT,
						mappingName: ANALOG_BJT.POLARITY
					},
					{
						dataSource: TABLE_DATA_SOURCE.PRT,
						mappingName: ANALOG_BJT.I_C_CONT
					},
					{
						dataSource: TABLE_DATA_SOURCE.PRT,
						mappingName: ANALOG_BJT.V_CEO_MIN
					},
					{
						dataSource: TABLE_DATA_SOURCE.PRT,
						mappingName: ANALOG_BJT.V_EBO
					},
					{
						dataSource: TABLE_DATA_SOURCE.PRT,
						mappingName: ANALOG_BJT.V_CBO
					},
					{
						dataSource: TABLE_DATA_SOURCE.PRT,
						mappingName: ANALOG_BJT.V_CE_SAT
					},
					{
						dataSource: TABLE_DATA_SOURCE.PRT,
						mappingName: ANALOG_BJT.V_BE_SAT
					},
					{
						dataSource: TABLE_DATA_SOURCE.PRT,
						mappingName: ANALOG_BJT.V_BE_ON
					},
					{
						dataSource: TABLE_DATA_SOURCE.PRT,
						mappingName: ANALOG_BJT.H_FE_MAX
					},
					{
						dataSource: TABLE_DATA_SOURCE.PRT,
						mappingName: ANALOG_BJT.H_FE_MIN
					},
					{
						dataSource: TABLE_DATA_SOURCE.PRT,
						mappingName: ANALOG_BJT.F_T_MIN
					},
					{
						dataSource: TABLE_DATA_SOURCE.PRT,
						mappingName: ANALOG_BJT.P_TM_MAX
					}
				]
			},
			simulations: [
				{
					heading: 'Static characteristics - DC analysis',
					showRawData: false,
					inputHeading: 'Input parameters',
					note: 'Please note that simulation results are not clamped and may exceed device max ratings!',
					showInputParams: false,
					simulationLinks: [
						{
							heading: 'SPICE model link',
							/**
							 * link for simulation download
							 */
							link: this.modelLink
						}
					],
					/**
					 * defining netlist download icon and also path to netlist in spice project for whole simulation run
					 */
					netlist: {
						heading: 'Netlist',
						/**
						 * path to netlist in spice-simulator project (netlist used for this simulation)
						 */
						netlistPath: this.netlistPathDC,
						displayNetlist: true
					},
					schematic: {
						heading: 'Application schematic',
						schematicPath: this.SCHEMATIC_DC
					},
					inputParameters: [
						{
							/**
							 * base heading (heading is created from heading + unit)
							 */
							heading: 'V<sub>CE<sub>(min)</sub></sub>',
							unit: UNITS_SIGN.VOLT,
							/**
							 * if unit is not base unit but for example it is in kilo ohm it is needed
							 * to add extension for simulator which tells that it is kilo that’s mean ‘k’
							 */
							netlistUnit: '',
							/**
							 * exact name of parameter from netlist to be replaced
							 */
							mappingName: 'VceMin',
							minBoundary: 0.2,
							maxBoundary: 80,
							defaultValue: 1
						},
						{
							heading: 'V<sub>CE<sub>(max)</sub></sub>',
							unit: UNITS_SIGN.VOLT,
							netlistUnit: '',
							mappingName: 'VceMax',
							minBoundary: 0.1,
							maxBoundary: 80,
							defaultValue: 10
						},
						{
							heading: 'V<sub>CE<sub>(increment)</sub></sub>',
							unit: UNITS_SIGN.VOLT,
							netlistUnit: '',
							mappingName: 'VceStep',
							minBoundary: 1,
							maxBoundary: 40,
							defaultValue: 2
						},
						{
							heading: 'I<sub>B<sub>(min)</sub></sub>',
							unit: UNITS_SIGN.MILLI_AMPER,
							netlistUnit: 'm',
							mappingName: 'IbMin',
							minBoundary: 0.001,
							maxBoundary: 49,
							defaultValue: 0.002
						},
						{
							heading: 'I<sub>B<sub>(max)</sub></sub>',
							unit: UNITS_SIGN.MILLI_AMPER,
							netlistUnit: 'm',
							mappingName: 'IbMax',
							minBoundary: 0.002,
							maxBoundary: 50,
							defaultValue: 30
						},
						{
							heading: 'I<sub>B<sub>(increment)</sub></sub>',
							unit: UNITS_SIGN.MILLI_AMPER,
							netlistUnit: 'm',
							mappingName: 'IbStep',
							minBoundary: 1,
							maxBoundary: 50,
							defaultValue: 2
						},
						{
							heading: 't<sub>ambient<sub>',
							unit: UNITS_SIGN.CELSIUS,
							netlistUnit: '',
							mappingName: 'Tset',
							minBoundary: -65,
							maxBoundary: 150,
							defaultValue: 25,
							displayInput: true
						}
					],
					charts: [
						{
							/**
							 * ---------------------------------------------------I_C vs V_CE ---------------------------------------------------
							 */
							id: 'IcVsVce',
							mainHeading:
								'I[baseline-shift: sub; font-size: 11px;]COLLECTOR[/] = f (V[baseline-shift: sub; font-size: 11px;]CE[/], I[baseline-shift: sub; font-size: 11px;]BASE[/])',
							chartType: CHART_TYPE.LINE_CHART,
							simulationType: SIMULATION_TYPE.DC,
							visible: true,
							axis: [
								{
									id: ID_GRAPH_AXIS.X,
									heading: 'V[baseline-shift: sub; font-size: 11px;]CE[/]',
									unit: UNITS_SIGN.VOLT,
									columnNumber: this.DC_CHART_COLUMN_MAPPING.V_COLLECTOR
								},
								{
									id: ID_GRAPH_AXIS.Y1,
									heading: 'I[baseline-shift: sub; font-size: 11px;]C[/]',
									unit: UNITS_SIGN.AMPER,
									columnNumber: this.DC_CHART_COLUMN_MAPPING.I_COLLECTOR
								}
							],
							series: {
								/**
								 * Here graph parametrization is defined
								 */
								heading: 'I[baseline-shift: sub;font-size: 11px;]B[/]',
								unit: UNITS_SIGN.AMPER,
								columnNumber: this.DC_CHART_COLUMN_MAPPING.I_BASE
							}
						},
						{
							/**
							 * ---------------------------------------------------I_C vs I_B ---------------------------------------------------
							 */
							id: 'IcVsIb',
							mainHeading:
								'I[baseline-shift: sub; font-size: 11px;]COLLECTOR[/] = f (I[baseline-shift: sub; font-size: 11px;]BASE[/], V[baseline-shift: sub; font-size: 11px;]CE[/])',
							chartType: CHART_TYPE.LINE_CHART,
							simulationType: SIMULATION_TYPE.DC,
							visible: true,
							axis: [
								{
									id: ID_GRAPH_AXIS.X,
									heading: 'I[baseline-shift: sub; font-size: 11px;]B[/]',
									unit: UNITS_SIGN.AMPER,
									columnNumber: this.DC_CHART_COLUMN_MAPPING.I_BASE
								},
								{
									id: ID_GRAPH_AXIS.Y1,
									heading: 'I[baseline-shift: sub; font-size: 11px;]C[/]',
									unit: UNITS_SIGN.AMPER,
									columnNumber: this.DC_CHART_COLUMN_MAPPING.I_COLLECTOR
								}
							],
							series: {
								/**
								 * Here graph parametrization is defined
								 */
								heading: 'V[baseline-shift: sub;font-size: 11px;]CE[/]',
								unit: UNITS_SIGN.VOLT,
								columnNumber: this.DC_CHART_COLUMN_MAPPING.V_COLLECTOR
							}
						},
						{
							/**
							 * ---------------------------------------------------V_BE vs I_C ---------------------------------------------------
							 */
							id: 'VbeVsIc',
							mainHeading:
								'V[baseline-shift: sub; font-size: 11px;]BASE[/] = f (I[baseline-shift: sub; font-size: 11px;]COLLECTOR[/], V[baseline-shift: sub; font-size: 11px;]CE[/])',
							chartType: CHART_TYPE.LINE_CHART,
							simulationType: SIMULATION_TYPE.DC,
							visible: true,
							axis: [
								{
									id: ID_GRAPH_AXIS.X,
									heading: 'I[baseline-shift: sub; font-size: 11px;]C[/]',
									unit: UNITS_SIGN.AMPER,
									columnNumber: this.DC_CHART_COLUMN_MAPPING.I_COLLECTOR
								},
								{
									id: ID_GRAPH_AXIS.Y1,
									heading: 'V[baseline-shift: sub; font-size: 11px;]BE[/]',
									unit: UNITS_SIGN.VOLT,
									columnNumber: this.DC_CHART_COLUMN_MAPPING.V_BASE
								}
							],
							series: {
								/**
								 * Here graph parametrization is defined
								 */
								heading: 'V[baseline-shift: sub;font-size: 11px;]CE[/]',
								unit: UNITS_SIGN.VOLT,
								columnNumber: this.DC_CHART_COLUMN_MAPPING.V_COLLECTOR
							}
						},
						{
							/**
							 * ---------------------------------------------------h_FE vs I_C ---------------------------------------------------
							 */
							id: 'hFEVsIc',
							mainHeading:
								'h[baseline-shift: sub; font-size: 11px;]FE[/] = f (I[baseline-shift: sub; font-size: 11px;]COLLECTOR[/], V[baseline-shift: sub; font-size: 11px;]CE[/])',
							chartType: CHART_TYPE.LINE_CHART,
							simulationType: SIMULATION_TYPE.DC,
							visible: true,
							axis: [
								{
									id: ID_GRAPH_AXIS.X,
									heading: 'I[baseline-shift: sub; font-size: 11px;]C[/]',
									unit: UNITS_SIGN.AMPER,
									columnNumber: this.DC_CHART_COLUMN_MAPPING.I_COLLECTOR
								},
								{
									id: ID_GRAPH_AXIS.Y1,
									heading: 'h[baseline-shift: sub; font-size: 11px;]FE[/]',
									unit: UNITS_SIGN.NOUNITS,
									columnNumber: this.DC_CHART_COLUMN_MAPPING.h_FE
								}
							],
							series: {
								/**
								 * Here graph parametrization is defined
								 */
								heading: 'V[baseline-shift: sub;font-size: 11px;]CE[/]',
								unit: UNITS_SIGN.VOLT,
								columnNumber: this.DC_CHART_COLUMN_MAPPING.V_COLLECTOR
							}
						}
					]
				},
				{
					heading: 'Turn on / off sequence',
					showRawData: false,
					inputHeading: 'Input parameters',
					showInputParams: false,
					inputParameters: [
						{
							heading: 'I<sub>B<sub>(min)</sub></sub>',
							unit: UNITS_SIGN.MILLI_AMPER,
							netlistUnit: 'm',
							mappingName: 'IgenMin',
							minBoundary: 0,
							maxBoundary: 49,
							defaultValue: 0
						},
						{
							heading: 'I<sub>B<sub>(max)</sub></sub>',
							unit: UNITS_SIGN.MILLI_AMPER,
							netlistUnit: 'm',
							mappingName: 'IgenMax',
							minBoundary: 0.01,
							maxBoundary: 50,
							defaultValue: 30
						},
						{
							heading: 't<sub>rise<sub>I<sub>B</sub></sub></sub>',
							unit: UNITS_SIGN.NANO_SECOND,
							netlistUnit: 'n',
							mappingName: 'IgenTr',
							minBoundary: 5,
							maxBoundary: 100,
							defaultValue: 50
						},
						{
							heading: 't<sub>fall<sub>I<sub>B</sub></sub></sub>',
							unit: UNITS_SIGN.NANO_SECOND,
							netlistUnit: 'n',
							mappingName: 'IgenTf',
							minBoundary: 5,
							maxBoundary: 100,
							defaultValue: 50
						},
						{
							heading: 't<sub>ON<sub>I<sub>B</sub></sub></sub>',
							unit: UNITS_SIGN.NANO_SECOND,
							netlistUnit: 'n',
							mappingName: 'IgenTon',
							minBoundary: 10,
							maxBoundary: 1500,
							defaultValue: 500
						},
						{
							heading: 't<sub>IB<sub>delay</sub></sub>',
							unit: UNITS_SIGN.NANO_SECOND,
							netlistUnit: 'n',
							mappingName: 'IgenDelay',
							minBoundary: 5,
							maxBoundary: 100,
							defaultValue: 50
						},
						{
							heading: 't<sub>step<sub>max</sub></sub>',
							unit: UNITS_SIGN.NANO_SECOND,
							netlistUnit: 'n',
							mappingName: 'Tstep',
							minBoundary: 5,
							maxBoundary: 50,
							defaultValue: 10
						},
						{
							heading: 'V<sub>CE<sub>(min)</sub></sub>',
							unit: UNITS_SIGN.VOLT,
							netlistUnit: '',
							mappingName: 'VsMin',
							minBoundary: 0,
							maxBoundary: 79,
							defaultValue: 1
						},
						{
							heading: 'V<sub>CE<sub>(max)</sub></sub>',
							unit: UNITS_SIGN.VOLT,
							netlistUnit: '',
							mappingName: 'VsMax',
							minBoundary: 1,
							maxBoundary: 80,
							defaultValue: 10
						},
						{
							heading: 'V<sub>CE<sub>(num.steps)</sub></sub>',
							unit: UNITS_SIGN.NOUNITS,
							netlistUnit: '',
							mappingName: 'nVsStep',
							minBoundary: 1,
							maxBoundary: 5,
							defaultValue: 3,
							isInteger: true
						},
						{
							heading: 'R<sub>B<sub>',
							unit: UNITS_SIGN.MILLI_OHM,
							netlistUnit: 'm',
							mappingName: 'Rb',
							minBoundary: 0.1,
							maxBoundary: 100,
							defaultValue: 1
						},
						{
							heading: 'R<sub>C<sub>',
							unit: UNITS_SIGN.MILLI_OHM,
							netlistUnit: 'm',
							mappingName: 'Rc',
							minBoundary: 0.1,
							maxBoundary: 100,
							defaultValue: 1
						},
						{
							heading: 'R<sub>E<sub>',
							unit: UNITS_SIGN.MILLI_OHM,
							netlistUnit: 'm',
							mappingName: 'Re',
							minBoundary: 0.1,
							maxBoundary: 100,
							defaultValue: 1
						},
						{
							heading: 'C<sub>CE<sub>',
							unit: UNITS_SIGN.PICO_FARAD,
							netlistUnit: 'p',
							mappingName: 'Cce',
							minBoundary: 1,
							maxBoundary: 1000,
							defaultValue: 100
						},
						{
							heading: 'C<sub>BE<sub>',
							unit: UNITS_SIGN.PICO_FARAD,
							netlistUnit: 'p',
							mappingName: 'Cbe',
							minBoundary: 1,
							maxBoundary: 1000,
							defaultValue: 100
						},
						{
							heading: 'C<sub>BC<sub>',
							unit: UNITS_SIGN.PICO_FARAD,
							netlistUnit: 'p',
							mappingName: 'Cbc',
							minBoundary: 1,
							maxBoundary: 1000,
							defaultValue: 100
						},
						{
							heading: 'L<sub>B<sub>',
							unit: UNITS_SIGN.NANO_HENRY,
							netlistUnit: 'n',
							mappingName: 'Lb',
							minBoundary: 0.01,
							maxBoundary: 100,
							defaultValue: 0.5
						},
						{
							heading: 'L<sub>C<sub>',
							unit: UNITS_SIGN.NANO_HENRY,
							netlistUnit: 'n',
							mappingName: 'Lc',
							minBoundary: 0.01,
							maxBoundary: 100,
							defaultValue: 0.5
						},
						{
							heading: 'L<sub>E<sub>',
							unit: UNITS_SIGN.NANO_HENRY,
							netlistUnit: 'n',
							mappingName: 'Le',
							minBoundary: 0.01,
							maxBoundary: 100,
							defaultValue: 0.5
						},
						{
							heading: 't<sub>ambient<sub>',
							unit: UNITS_SIGN.CELSIUS,
							netlistUnit: '',
							mappingName: 'Tambient',
							minBoundary: -65,
							maxBoundary: 150,
							defaultValue: 25,
							displayInput: true
						}
					],
					note: 'Results are linearly decimated based on t<sub>step<sub>max</sub></sub> parameter.',
					netlist: {
						heading: 'Netlist',
						netlistPath: this.netlistPathTran,
						displayNetlist: true
					},
					schematic: {
						heading: 'Application schematic',
						schematicPath: this.SCHEMATIC_TRAN
					},
					charts: [
						{
							id: 'IbVsTime',
							mainHeading:
								'I[baseline-shift: sub; font-size: 11px;]BASE[/] = f (t, V[baseline-shift: sub;font-size: 11px;]COLLECTOR[/])',
							chartType: CHART_TYPE.LINE_CHART,
							simulationType: SIMULATION_TYPE.TRANSIENT,
							visible: true,
							axis: [
								{
									id: ID_GRAPH_AXIS.X,
									heading: 'time',
									unit: UNITS_SIGN.SECOND,
									columnNumber: this.TRANSIENT_CHART_COLUMN_MAPPING.TIME
								},
								{
									id: ID_GRAPH_AXIS.Y1,
									heading: 'I[baseline-shift: sub; font-size: 11px;]BASE[/]',
									unit: UNITS_SIGN.AMPER,
									columnNumber: this.TRANSIENT_CHART_COLUMN_MAPPING.I_BASE
								}
							],
							series: {
								heading: 'V[baseline-shift: sub;font-size: 11px;]COLLECTOR[/]',
								unit: UNITS_SIGN.VOLT,
								columnNumber: this.TRANSIENT_CHART_COLUMN_MAPPING.Vs_STEP
							}
						},
						{
							id: 'VbaseVsTime',
							mainHeading:
								'V[baseline-shift: sub; font-size: 11px;]BASE[/] = f (t, V[baseline-shift: sub;font-size: 11px;]COLLECTOR[/])',
							chartType: CHART_TYPE.LINE_CHART,
							simulationType: SIMULATION_TYPE.TRANSIENT,
							visible: true,
							axis: [
								{
									id: ID_GRAPH_AXIS.X,
									heading: 'time',
									unit: UNITS_SIGN.SECOND,
									columnNumber: this.TRANSIENT_CHART_COLUMN_MAPPING.TIME
								},
								{
									id: ID_GRAPH_AXIS.Y1,
									heading: 'V[baseline-shift: sub; font-size: 11px;]BASE[/]',
									unit: UNITS_SIGN.VOLT,
									columnNumber: this.TRANSIENT_CHART_COLUMN_MAPPING.V_BASE
								}
							],
							series: {
								heading: 'V[baseline-shift: sub;font-size: 11px;]COLLECTOR[/]',
								unit: UNITS_SIGN.VOLT,
								columnNumber: this.TRANSIENT_CHART_COLUMN_MAPPING.Vs_STEP
							}
						},
						{
							id: 'VemitterVsTime',
							mainHeading:
								'V[baseline-shift: sub; font-size: 11px;]EMITTER[/] = f (t, V[baseline-shift: sub;font-size: 11px;]COLLECTOR[/])',
							chartType: CHART_TYPE.LINE_CHART,
							simulationType: SIMULATION_TYPE.TRANSIENT,
							visible: true,
							axis: [
								{
									id: ID_GRAPH_AXIS.X,
									heading: 'time',
									unit: UNITS_SIGN.SECOND,
									columnNumber: this.TRANSIENT_CHART_COLUMN_MAPPING.TIME
								},
								{
									id: ID_GRAPH_AXIS.Y1,
									heading: 'V[baseline-shift: sub; font-size: 11px;]EMITTER[/]',
									unit: UNITS_SIGN.VOLT,
									columnNumber: this.TRANSIENT_CHART_COLUMN_MAPPING.V_EMITTER
								}
							],
							series: {
								heading: 'V[baseline-shift: sub;font-size: 11px;]COLLECTOR[/]',
								unit: UNITS_SIGN.VOLT,
								columnNumber: this.TRANSIENT_CHART_COLUMN_MAPPING.Vs_STEP
							}
						},
						{
							id: 'IcVsTime',
							mainHeading:
								'I[baseline-shift: sub; font-size: 11px;]COLLECTOR[/] = f (t, V[baseline-shift: sub;font-size: 11px;]COLLECTOR[/])',
							chartType: CHART_TYPE.LINE_CHART,
							simulationType: SIMULATION_TYPE.TRANSIENT,
							visible: true,
							axis: [
								{
									id: ID_GRAPH_AXIS.X,
									heading: 'time',
									unit: UNITS_SIGN.SECOND,
									columnNumber: this.TRANSIENT_CHART_COLUMN_MAPPING.TIME
								},
								{
									id: ID_GRAPH_AXIS.Y1,
									heading: 'I[baseline-shift: sub; font-size: 11px;]COLLECTOR[/]',
									unit: UNITS_SIGN.AMPER,
									columnNumber: this.TRANSIENT_CHART_COLUMN_MAPPING.I_SUPPLY
								}
							],
							series: {
								heading: 'V[baseline-shift: sub;font-size: 11px;]COLLECTOR[/]',
								unit: UNITS_SIGN.VOLT,
								columnNumber: this.TRANSIENT_CHART_COLUMN_MAPPING.Vs_STEP
							}
						},
						{
							id: 'VceVsTime',
							mainHeading:
								'V[baseline-shift: sub; font-size: 11px;]CE[/] = f (t, V[baseline-shift: sub;font-size: 11px;]COLLECTOR[/])',
							chartType: CHART_TYPE.LINE_CHART,
							simulationType: SIMULATION_TYPE.TRANSIENT,
							visible: true,
							axis: [
								{
									id: ID_GRAPH_AXIS.X,
									heading: 'time',
									unit: UNITS_SIGN.SECOND,
									columnNumber: this.TRANSIENT_CHART_COLUMN_MAPPING.TIME
								},
								{
									id: ID_GRAPH_AXIS.Y1,
									heading: 'V[baseline-shift: sub; font-size: 11px;]CE[/]',
									unit: UNITS_SIGN.VOLT,
									columnNumber: this.TRANSIENT_CHART_COLUMN_MAPPING.V_CE
								}
							],
							series: {
								heading: 'V[baseline-shift: sub;font-size: 11px;]COLLECTOR[/]',
								unit: UNITS_SIGN.VOLT,
								columnNumber: this.TRANSIENT_CHART_COLUMN_MAPPING.Vs_STEP
							}
						},
						{
							id: 'PdVsTime',
							mainHeading:
								'P[baseline-shift: sub; font-size: 11px;]Dissipation(QT)[/] = f (t, V[baseline-shift: sub;font-size: 11px;]COLLECTOR[/])',
							chartType: CHART_TYPE.LINE_CHART,
							simulationType: SIMULATION_TYPE.TRANSIENT,
							visible: true,
							axis: [
								{
									id: ID_GRAPH_AXIS.X,
									heading: 'time',
									unit: UNITS_SIGN.SECOND,
									columnNumber: this.TRANSIENT_CHART_COLUMN_MAPPING.TIME
								},
								{
									id: ID_GRAPH_AXIS.Y1,
									heading: 'P[baseline-shift: sub; font-size: 11px;]D[/]',
									unit: UNITS_SIGN.WATT,
									columnNumber: this.TRANSIENT_CHART_COLUMN_MAPPING.P_D
								}
							],
							series: {
								heading: 'V[baseline-shift: sub;font-size: 11px;]COLLECTOR[/]',
								unit: UNITS_SIGN.VOLT,
								columnNumber: this.TRANSIENT_CHART_COLUMN_MAPPING.Vs_STEP
							}
						}
					]
				}
			]
		};

		return _.merge({}, defaultDefinition, customizations);
	}
}
